import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { authService, type AuthUser, type SignOutReason } from '@/services/authService'
import {
  onSessionExpiryChange,
  refreshSession,
  SESSION_PROBE_RETRY_MS,
  SESSION_WARNING_LEAD_MS,
} from '@/services/sessionRefresh'
import { SessionTimeoutModal } from '@/components/SessionTimeoutModal'

interface AuthContextType {
  currentUser: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  /** True when the session check failed because the server was unreachable (network/5xx), not because the user is signed out. */
  connectionLost: boolean
  /** ISO instant at which the server signs an inactive user out; null until known. */
  sessionExpiresAt: string | null
  retrySession: () => void
  login: () => void
  logout: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

/** The longest delay setTimeout honours; longer values wrap and fire at once. */
const MAX_TIMER_MS = 2 ** 31 - 1

/**
 * Who is signed in — and, since identity became a short-lived token rather than a server session,
 * for how much longer (docs/plans/jwt-cookie-auth.md, D3/D4).
 *
 * The server tells us the deadline (`sessionExpiresAt` on `/auth/me` and on every refresh): the
 * moment it will stop honouring a token refresh, which is when an inactive user is signed out. This
 * provider counts down to it. Two minutes before, it asks the server whether the sign-in is still
 * alive — another tab may have refreshed it, moving the deadline — and only if the token has really
 * expired does it show the warning. At the deadline it asks once more and, if the answer is still
 * "expired", signs the user out through the normal logout so Cognito's own session ends too and the
 * landing page can say why. Timers are unreliable in background tabs, so the tab re-evaluates
 * whenever it becomes visible again.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [connectionLost, setConnectionLost] = useState(false)
  const [sessionExpiresAt, setSessionExpiresAt] = useState<string | null>(null)
  const [timeoutWarning, setTimeoutWarning] = useState(false)

  const checkSession = useCallback((forceReload = false) => {
    setIsLoading(true)
    // Initial load shares the cached fetch with the route guards (a single /auth/me); a manual
    // retry or a fresh sign-in forces a new fetch and replaces the cache.
    const session = forceReload ? authService.reloadSession() : authService.ensureSession()
    session.then((result) => {
      console.log('[AuthContext] Session check result:', result.status)
      if (result.status === 'authenticated') {
        setCurrentUser(result.user)
        setIsAuthenticated(true)
        setConnectionLost(false)
        setSessionExpiresAt(result.user.sessionExpiresAt ?? null)
      } else if (result.status === 'unreachable') {
        // Keep whatever user we had; the server is down, the session may still be valid.
        setConnectionLost(true)
      } else {
        setCurrentUser(null)
        setIsAuthenticated(false)
        setConnectionLost(false)
        setSessionExpiresAt(null)
      }
      setIsLoading(false)
    })
  }, [])

  useEffect(() => {
    checkSession()
  }, [checkSession])

  const signOut = useCallback((reason?: SignOutReason) => {
    setTimeoutWarning(false)
    setCurrentUser(null)
    setIsAuthenticated(false)
    setSessionExpiresAt(null)
    authService.clearCachedSession()
    authService.logout(reason)
  }, [])

  /** A new deadline from the server (a refresh here or in another tab, or a probe): start over from it. */
  const adoptDeadline = useCallback((iso: string) => {
    setTimeoutWarning(false)
    setSessionExpiresAt(iso)
  }, [])

  // Every successful refresh — from the API wrapper's 401 retry, an SSE re-open, or the dialog's
  // "Stay signed in" — announces the new deadline.
  useEffect(() => onSessionExpiryChange(adoptDeadline), [adoptDeadline])

  useEffect(() => {
    if (!isAuthenticated || !sessionExpiresAt) return
    const deadline = Date.parse(sessionExpiresAt)
    if (Number.isNaN(deadline)) return

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    // setTimeout overflows past 2^31-1 ms (about 24.8 days) and fires IMMEDIATELY instead; a far-off
    // deadline would then spin the effect in a tight loop. Sleep in bounded hops; `evaluate` simply
    // reschedules when it wakes early.
    const schedule = (at: number) => {
      clearTimeout(timer)
      timer = setTimeout(() => void evaluate(), Math.min(Math.max(0, at - Date.now()), MAX_TIMER_MS))
    }

    const evaluate = async () => {
      if (cancelled) return
      const warnAt = deadline - SESSION_WARNING_LEAD_MS
      if (Date.now() < warnAt) {
        schedule(warnAt)
        return
      }
      // Ask the server rather than trusting this tab's picture: another tab may have refreshed.
      // No `refreshIfExpired` here: the probe asks whether the sign-in is alive and must never
      // extend it, or an idle tab would keep itself signed in (D5).
      const probe = await authService.getSession()
      if (cancelled) return
      if (probe.status === 'authenticated') {
        const fresh = Date.parse(probe.user.sessionExpiresAt)
        if (fresh > deadline) {
          adoptDeadline(probe.user.sessionExpiresAt)
          return
        }
        // The token is still valid by the server's clock; look again shortly.
        schedule(Date.now() + SESSION_PROBE_RETRY_MS)
        return
      }
      if (probe.status === 'unreachable') {
        // No answer is not a sign-out (the connection-lost banner covers that); keep asking.
        schedule(Date.now() + SESSION_PROBE_RETRY_MS)
        return
      }
      // 401: the token has expired. Inside the grace we warn; at the deadline we sign out.
      if (Date.now() >= deadline) {
        signOut('timeout')
        return
      }
      setTimeoutWarning(true)
      schedule(deadline)
    }

    // Background tabs throttle or suspend timers: re-evaluate the moment the tab is visible again.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void evaluate()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    void evaluate()

    return () => {
      cancelled = true
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [isAuthenticated, sessionExpiresAt, adoptDeadline, signOut])

  const retrySession = useCallback(() => {
    checkSession(true)
  }, [checkSession])

  const login = () => {
    checkSession(true)
  }

  const logout = () => {
    signOut()
  }

  /**
   * The dialog's "Stay signed in": a refresh is the one user action that counts as activity. A
   * refusal means the sign-in is over; an identity-provider outage leaves the dialog up — the
   * deadline probe keeps asking, and the user can try the button again.
   */
  const staySignedIn = useCallback(async () => {
    const outcome = await refreshSession()
    if (outcome === 'refused') signOut('timeout')
  }, [signOut])

  return (
    <AuthContext.Provider
      value={{ currentUser, isAuthenticated, isLoading, connectionLost, sessionExpiresAt, retrySession, login, logout }}
    >
      {children}
      {timeoutWarning && isAuthenticated && sessionExpiresAt && (
        // Mounted only while warning, so each showing starts its countdown from a fresh clock.
        <SessionTimeoutModal
          deadline={Date.parse(sessionExpiresAt)}
          onStaySignedIn={() => void staySignedIn()}
          onSignOut={logout}
        />
      )}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
